import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";

import { queryClient } from "@/lib/queryClient";
import { isIndexedDBAvailable } from "@/lib/clientStorage";
import { hasOptionalStorageConsent } from "@/lib/privacyConsent";
import {
  asyncStoragePersister,
  queryPersistBuster,
} from "@/lib/persist/queryPersister";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";

const PERSIST_MAX_AGE_MS = 1000 * 60 * 60 * 24;

function shouldEnableQueryPersist(): boolean {
  return hasOptionalStorageConsent() && isIndexedDBAvailable();
}

/**
 * Uses IndexedDB offline cache only when the user opted in and the browser
 * allows it; otherwise a plain QueryClientProvider (always fresh network).
 */
export function QueryPersistenceGate({ children }: { children: React.ReactNode }) {
  const enablePersist = shouldEnableQueryPersist();

  if (!enablePersist) {
    return (
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    );
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        buster: queryPersistBuster(),
        maxAge: PERSIST_MAX_AGE_MS,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            query.queryKey[0] === trackedDaysPrefix[0],
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
